import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Upload, Loader2, Pencil, ImageIcon, Utensils, LayoutList, IndianRupee, Megaphone, GripVertical, Check, Star, MessageSquareQuote, ClipboardList, Download, Search, X, CalendarDays, Repeat, MessageCircle, ChefHat } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail, waCustomerLink } from "@/lib/api";
import { generateReceiptPDF } from "@/lib/receipt";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DatePopover } from "@/components/site/DatePopover";

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");
const field = "w-full h-11 rounded-lg border border-border bg-background px-3 outline-none focus:border-primary text-base";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === false) {
    navigate("/admin/login");
    return null;
  }
  if (user === null) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-10 bg-primary text-primary-foreground px-5 h-14 flex items-center justify-between">
        <span className="font-serif text-xl">The Quisine · Admin</span>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }}
          className="flex items-center gap-2 text-sm rounded-full border border-primary-foreground/30 px-4 h-9">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <Tabs defaultValue="orders">
          <TabsList className="w-full flex overflow-x-auto no-scrollbar h-auto gap-1">
            <TabsTrigger value="orders" data-testid="tab-orders" className="gap-1.5 py-2 shrink-0"><ClipboardList className="w-4 h-4" /> Orders</TabsTrigger>
            <TabsTrigger value="images" data-testid="tab-images" className="gap-1.5 py-2 shrink-0"><ImageIcon className="w-4 h-4" /> Images</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="gap-1.5 py-2 shrink-0"><ImageIcon className="w-4 h-4" /> Gallery</TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-packages" className="gap-1.5 py-2 shrink-0"><LayoutList className="w-4 h-4" /> Packages</TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu" className="gap-1.5 py-2 shrink-0"><Utensils className="w-4 h-4" /> Menu</TabsTrigger>
            <TabsTrigger value="bulkmenu" data-testid="tab-bulkmenu" className="gap-1.5 py-2 shrink-0"><ChefHat className="w-4 h-4" /> Bulk Menu</TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-pricing" className="gap-1.5 py-2 shrink-0"><IndianRupee className="w-4 h-4" /> Pricing</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className="gap-1.5 py-2 shrink-0"><MessageSquareQuote className="w-4 h-4" /> Reviews</TabsTrigger>
            <TabsTrigger value="offer" data-testid="tab-offer" className="gap-1.5 py-2 shrink-0"><Megaphone className="w-4 h-4" /> Offer</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-5"><OrdersManager /></TabsContent>
          <TabsContent value="images" className="mt-5"><SiteImagesManager /></TabsContent>
          <TabsContent value="gallery" className="mt-5"><GalleryManager /></TabsContent>
          <TabsContent value="packages" className="mt-5"><PackagesManager /></TabsContent>
          <TabsContent value="menu" className="mt-5"><MenuManager /></TabsContent>
          <TabsContent value="bulkmenu" className="mt-5"><BulkMenuManager /></TabsContent>
          <TabsContent value="rates" className="mt-5"><PricingManager /></TabsContent>
          <TabsContent value="reviews" className="mt-5"><ReviewsManager /></TabsContent>
          <TabsContent value="offer" className="mt-5"><OfferManager /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ---------------- Orders (bookings) ---------------- */
const SITE_IMAGE_SLOTS = [
  { key: "hero", label: "Hero banner (top of homepage)", ratio: "aspect-[16/9]" },
  { key: "service_bulk", label: "Bulk Food card", ratio: "aspect-[16/10]" },
  { key: "service_catering", label: "Catering card", ratio: "aspect-[16/10]" },
];

function SiteImagesManager() {
  const [images, setImages] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const load = () => api.get("/settings/site-images").then((r) => setImages(r.data || {})).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusyKey(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/settings/site-images/${key}`, { storage_path: data.path });
      toast.success("Photo updated");
      load();
    } catch { toast.error("Upload failed"); } finally { setBusyKey(""); }
    e.target.value = "";
  };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Change the photos shown in different sections of the homepage. If a slot is empty, the default photo is used.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {SITE_IMAGE_SLOTS.map((s) => (
          <div key={s.key} data-testid={`site-image-slot-${s.key}`} className="rounded-xl border border-border bg-card overflow-hidden">
            <div className={`relative ${s.ratio} bg-secondary/40`}>
              {images[s.key] ? (
                <img data-testid={`site-image-preview-${s.key}`} src={imgUrl(images[s.key])} alt={s.label} className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-muted-foreground text-xs px-4 text-center">Using default photo</div>
              )}
            </div>
            <div className="p-3">
              <div className="text-sm font-medium">{s.label}</div>
              <label className="mt-2 flex items-center justify-center gap-2 h-10 rounded-lg border border-border text-sm font-medium cursor-pointer hover:bg-secondary/50 transition-colors">
                {busyKey === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {images[s.key] ? "Replace photo" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" data-testid={`site-image-upload-${s.key}`} onChange={(e) => upload(s.key, e)} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtDateHeading(dateStr) {
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return dateStr; }
}

function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [range, setRange] = useState("all");
  const [limit, setLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [ovDate, setOvDate] = useState("");
  const [ovLimit, setOvLimit] = useState("");
  const [ovLabel, setOvLabel] = useState("");
  const [savingOv, setSavingOv] = useState(false);

  useEffect(() => {
    api.get("/admin/bookings").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/settings/slots").then((r) => setLimit(r.data.max_per_day)).catch(() => {});
    api.get("/admin/slots/overrides").then((r) => setOverrides(r.data)).catch(() => {});
  }, []);

  const saveLimit = async () => {
    setSavingLimit(true);
    try { await api.put("/admin/settings/slots", { max_per_day: Number(limit) || 1 }); toast.success("Daily booking limit updated"); }
    catch { toast.error("Save failed"); } finally { setSavingLimit(false); }
  };

  const addOverride = async () => {
    if (!ovDate) { toast.error("Pick a date"); return; }
    if (ovLimit === "" || Number(ovLimit) < 0) { toast.error("Enter a valid limit (0 or more)"); return; }
    setSavingOv(true);
    try {
      await api.put("/admin/slots/overrides", { date: ovDate, limit: Number(ovLimit), label: ovLabel.trim() });
      const r = await api.get("/admin/slots/overrides");
      setOverrides(r.data);
      setOvDate(""); setOvLimit(""); setOvLabel("");
      toast.success("Special-date cap saved");
    } catch { toast.error("Save failed"); } finally { setSavingOv(false); }
  };

  const removeOverride = async (date) => {
    try {
      await api.delete(`/admin/slots/overrides/${date}`);
      setOverrides((o) => o.filter((x) => x.date !== date));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const istToday = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const weekEnd = (() => { const d = new Date(istToday); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); })();

  const term = q.trim().toLowerCase();
  // Count paid bookings per phone (across all orders) to flag returning customers.
  const phoneCounts = orders.reduce((m, b) => {
    const p = (b.phone || "").trim();
    if (p) m[p] = (m[p] || 0) + 1;
    return m;
  }, {});
  const filtered = orders.filter((b) => {
    const matchesText = !term ||
      (b.name || "").toLowerCase().includes(term) ||
      (b.phone || "").toLowerCase().includes(term) ||
      (b.receipt_no || "").toLowerCase().includes(term);
    const matchesDate = !dateFilter || b.event_date === dateFilter;
    const inRange = range === "all" ||
      (range === "today" && b.event_date === istToday) ||
      (range === "week" && b.event_date >= istToday && b.event_date <= weekEnd);
    return matchesText && matchesDate && inRange;
  });

  const RANGES = [{ k: "all", l: "All" }, { k: "today", l: "Today" }, { k: "week", l: "This week" }];

  // group by event_date, sort ascending by date
  const groups = {};
  filtered.forEach((b) => { (groups[b.event_date] = groups[b.event_date] || []).push(b); });
  const dates = Object.keys(groups).sort();
  dates.forEach((d) => groups[d].sort((a, b) => (a.event_time || "").localeCompare(b.event_time || "")));

  return (
    <div>
      {/* Range tabs + daily cap */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-full border border-border bg-card p-1 w-fit">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`orders-range-${r.k}`} onClick={() => setRange(r.k)}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${range === r.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{r.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Max bookings / day</span>
          <input data-testid="slot-limit-input" type="number" min="1" value={limit} onChange={(e) => setLimit(e.target.value)}
            className="w-16 h-9 rounded-lg border border-border bg-background px-2 text-sm text-center outline-none focus:border-primary" />
          <button data-testid="slot-limit-save" onClick={saveLimit} disabled={savingLimit}
            className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">{savingLimit ? "…" : "Save"}</button>
        </div>
      </div>

      {/* Festival / special-date caps */}
      <div data-testid="date-overrides-panel" className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">Festival & special-date caps</h4>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Set a different booking limit for busy dates (e.g. Diwali, wedding days). Use 0 to close a date. These override the daily limit above.</p>

        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input data-testid="override-date" type="date" value={ovDate} onChange={(e) => setOvDate(e.target.value)}
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm outline-none focus:border-primary" />
          <input data-testid="override-limit" type="number" min="0" placeholder="Cap" value={ovLimit} onChange={(e) => setOvLimit(e.target.value)}
            className="w-20 h-9 rounded-lg border border-border bg-background px-2 text-sm text-center outline-none focus:border-primary" />
          <input data-testid="override-label" placeholder="Label (e.g. Diwali)" value={ovLabel} onChange={(e) => setOvLabel(e.target.value)}
            className="flex-1 h-9 rounded-lg border border-border bg-background px-2 text-sm outline-none focus:border-primary" />
          <button data-testid="override-add" onClick={addOverride} disabled={savingOv}
            className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60 shrink-0">{savingOv ? "…" : "Add / Update"}</button>
        </div>

        {overrides.length > 0 && (
          <ul data-testid="override-list" className="mt-3 space-y-1.5">
            {overrides.map((o) => (
              <li key={o.date} data-testid={`override-item-${o.date}`} className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{o.date}</span>
                  {o.label ? <span className="text-muted-foreground"> · {o.label}</span> : null}
                  <span className="text-muted-foreground"> · {o.limit === 0 ? "Closed" : `${o.limit} bookings`}</span>
                </span>
                <button data-testid={`override-delete-${o.date}`} onClick={() => removeOverride(o.date)} className="grid place-items-center w-8 h-8 rounded-full hover:bg-background text-destructive" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="orders-search" className={`${field} pl-9`} placeholder="Search name, phone or receipt #"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <DatePopover
            testId="orders-date-filter"
            value={dateFilter}
            onChange={setDateFilter}
            placeholder="Filter by date"
            className={`${field} min-w-[11rem] flex items-center gap-2`}
          />
          {(q || dateFilter) && (
            <button data-testid="orders-clear" onClick={() => { setQ(""); setDateFilter(""); }}
              className="grid place-items-center h-11 px-3 rounded-lg border border-border text-muted-foreground shrink-0"><X className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {!orders.length ? (
        <p className="text-sm text-muted-foreground py-6">No paid bookings yet. New orders appear here instantly.</p>
      ) : !filtered.length ? (
        <p data-testid="orders-empty" className="text-sm text-muted-foreground py-6">No bookings match your filters.</p>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">{filtered.length} booking{filtered.length > 1 ? "s" : ""} across {dates.length} date{dates.length > 1 ? "s" : ""}</p>
          {dates.map((date) => (
            <div key={date} data-testid={`orders-group-${date}`}>
              <div className="sticky top-14 z-[1] flex items-center gap-2 bg-secondary/80 backdrop-blur rounded-lg px-3 py-2 mb-2">
                <span className="font-serif text-lg text-primary">{fmtDateHeading(date)}</span>
                <span className="text-xs text-muted-foreground">· {groups[date].length} order{groups[date].length > 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-3">
                {groups[date].map((b) => (
                  <div key={b.id} data-testid={`order-${b.receipt_no}`} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-xl text-primary flex items-center gap-2 flex-wrap">
                          <span>{b.receipt_no} · {b.name}</span>
                          {phoneCounts[(b.phone || "").trim()] >= 2 && (
                            <span data-testid={`returning-badge-${b.receipt_no}`}
                              className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent-foreground border border-accent/30 px-2 py-0.5 text-[11px] font-sans font-semibold"
                              title="Returning customer — offer a loyalty perk">
                              <Repeat className="w-3 h-3" /> Returning · {phoneCounts[(b.phone || "").trim()]} orders
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{b.phone} · {b.persons} persons · {b.event_time}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{b.address}, {b.city} {b.pincode || ""}{b.without_onion_garlic ? " · No onion/garlic" : ""}</div>
                        {b.location_link ? <a href={b.location_link} target="_blank" rel="noreferrer" className="text-xs text-primary underline underline-offset-2 mt-0.5 inline-block">📍 Open delivery location on map</a> : null}
                        <div className="text-sm mt-1.5">Total {inr(b.total_amount)} · <span className="text-primary">Advance {inr(b.advance_paid)}</span> · Balance {inr(b.balance_due)}</div>
                        <div className="text-xs text-muted-foreground mt-1">{(b.items || []).map((i) => i.dish).join(", ")}</div>
                        {b.cooking_instructions ? <div className="text-xs text-muted-foreground mt-1">📝 {b.cooking_instructions}</div> : null}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button onClick={() => generateReceiptPDF(b)} className="grid place-items-center w-9 h-9 rounded-full border border-border" title="Download receipt">
                          <Download className="w-4 h-4" />
                        </button>
                        {Number(b.balance_due) > 0 && (
                          <a data-testid={`reminder-${b.receipt_no}`}
                            href={waCustomerLink(b.phone, `Hi ${b.name}, this is The Quisine. A gentle reminder: your balance of ${inr(b.balance_due)} for booking ${b.receipt_no} (event on ${b.event_date} at ${b.event_time}) is pending. Kindly complete it before the event. Thank you!`)}
                            target="_blank" rel="noreferrer"
                            className="grid place-items-center w-9 h-9 rounded-full bg-[#25D366] text-white" title="Send balance reminder on WhatsApp">
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Gallery ---------------- */
function GalleryManager() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/gallery").then((r) => setImages(r.data));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    try {
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Removed");
    load();
  };

  const saveCaption = async (img, caption) => {
    if (caption === img.caption) return;
    await api.put(`/admin/gallery/${img.id}`, { caption, order: img.order });
    toast.success("Caption saved");
    setImages((prev) => prev.map((i) => (i.id === img.id ? { ...i, caption } : i)));
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const from = images.findIndex((i) => i.id === dragId);
    const to = images.findIndex((i) => i.id === targetId);
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setImages(next);
    setDragId(null);
    await api.put("/admin/gallery-reorder", { ordered_ids: next.map((i) => i.id) });
    toast.success("Order saved");
  };

  return (
    <div>
      <label data-testid="gallery-upload-btn"
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 cursor-pointer text-sm font-medium">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "Uploading…" : "Upload image"}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
      </label>
      <p className="mt-2 text-xs text-muted-foreground">Drag the handle to reorder · edit captions below each photo.</p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {images.map((img) => (
          <div
            key={img.id}
            data-testid={`admin-gallery-${img.id}`}
            draggable
            onDragStart={() => setDragId(img.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(img.id)}
            className={`rounded-xl border bg-card overflow-hidden ${dragId === img.id ? "opacity-50 border-primary" : "border-border"}`}
          >
            <div className="relative aspect-square">
              <img src={imgUrl(img.storage_path)} alt={img.caption} className="w-full h-full object-cover" />
              <span className="absolute top-2 left-2 grid place-items-center w-8 h-8 rounded-full bg-background/90 cursor-grab active:cursor-grabbing">
                <GripVertical className="w-4 h-4" />
              </span>
              <button data-testid={`gallery-delete-${img.id}`} onClick={() => remove(img.id)}
                className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-destructive text-white">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <input
              data-testid={`gallery-caption-${img.id}`}
              defaultValue={img.caption}
              placeholder="Add caption…"
              onBlur={(e) => saveCaption(img, e.target.value)}
              className="w-full border-t border-border bg-background px-2.5 py-2 text-xs outline-none focus:bg-secondary/40"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Pricing (bulk food persons → price) ---------------- */
const emptyPrice = { persons: 10, price: 3200, order: 0 };

function PricingManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/pricing").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = { persons: Number(form.persons), price: Number(form.price), order: Number(form.persons) };
    try {
      if (form.id) await api.put(`/admin/pricing/${form.id}`, payload);
      else await api.post("/admin/pricing", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/pricing/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3">Bulk food price table. The calculator auto-interpolates prices for guest counts between these anchors.</p>
      <button data-testid="add-price-btn" onClick={() => setEditing({ ...emptyPrice })}
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 text-sm font-medium">
        <Plus className="w-4 h-4" /> Add price row
      </button>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div className="font-serif text-xl text-primary">{t.persons} persons · {inr(t.price)}</div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...t })} className="grid place-items-center w-9 h-9 rounded-full border border-border"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(t.id)} className="grid place-items-center w-9 h-9 rounded-full border border-border text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit" : "New"} price row</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Number of persons</label>
                <input className={`${field} mt-1`} type="number" value={editing.persons} onChange={(e) => setEditing({ ...editing, persons: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Total price (₹)</label>
                <input className={`${field} mt-1`} type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-price-btn" onClick={() => save(editing)} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Offer banner ---------------- */
function OfferManager() {
  const [offer, setOffer] = useState({ enabled: false, text: "", cta_text: "", start_date: "", end_date: "", active: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/offer").then((r) => setOffer(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/offer", offer);
      setOffer(data);
      toast.success("Offer updated");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl">
      <p className="text-sm text-muted-foreground mb-4">Show a festive strip at the top of the homepage. Perfect for wedding-season promos.</p>

      <div className={`mb-4 rounded-xl px-4 py-3 text-sm ${offer.active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}
        data-testid="offer-status">
        {offer.active ? "● Live on homepage right now" : "○ Not showing on homepage"}
        {offer.enabled && !offer.active && (offer.start_date || offer.end_date) ? " (outside scheduled dates)" : ""}
      </div>

      <label className="flex items-center justify-between rounded-xl border border-border bg-card p-4 cursor-pointer">
        <span>
          <span className="font-medium">Enable offer banner</span>
          <span className="block text-xs text-muted-foreground">Master switch — schedule below controls dates</span>
        </span>
        <input data-testid="offer-enabled" type="checkbox" className="w-5 h-5 accent-[#124f31]"
          checked={offer.enabled} onChange={(e) => setOffer({ ...offer, enabled: e.target.checked })} />
      </label>

      <div className="mt-4">
        <label className="text-sm font-medium">Banner text</label>
        <textarea data-testid="offer-text" className={`${field} h-20 py-2 mt-1`} placeholder="🎉 Wedding season special — book by June & save 10% on Gold & Platinum!"
          value={offer.text} onChange={(e) => setOffer({ ...offer, text: e.target.value })} />
      </div>
      <div className="mt-3">
        <label className="text-sm font-medium">Call-to-action label (optional)</label>
        <input data-testid="offer-cta" className={`${field} mt-1`} placeholder="Book now"
          value={offer.cta_text} onChange={(e) => setOffer({ ...offer, cta_text: e.target.value })} />
      </div>

      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <div className="text-sm font-medium">Auto-schedule (optional)</div>
        <p className="text-xs text-muted-foreground mt-0.5">Leave blank to run indefinitely while enabled. The banner turns on/off automatically between these dates.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Start date</label>
            <input data-testid="offer-start" type="date" className={`${field} mt-1`}
              value={offer.start_date || ""} onChange={(e) => setOffer({ ...offer, start_date: e.target.value })} />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">End date</label>
            <input data-testid="offer-end" type="date" className={`${field} mt-1`}
              value={offer.end_date || ""} onChange={(e) => setOffer({ ...offer, end_date: e.target.value })} />
          </div>
        </div>
      </div>

      <button data-testid="save-offer-btn" onClick={save} disabled={saving}
        className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save offer
      </button>
    </div>
  );
}

/* ---------------- Google reviews connection ---------------- */
function GoogleReviewsCard() {
  const [cfg, setCfg] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/settings/google").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!input.trim()) { toast.error("Paste a Google Maps link or Place ID"); return; }
    setBusy(true);
    const isPlaceId = input.trim().startsWith("ChIJ") || input.trim().startsWith("places/");
    try {
      const { data } = await api.post("/admin/settings/google", isPlaceId ? { place_id: input.trim() } : { maps_url: input.trim() });
      toast.success(`Connected · fetched ${data.fetched} review(s)`);
      setInput(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/reviews/refresh-google"); toast.success(`Refreshed · ${data.fetched} review(s)`); load(); }
    catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-5">
      <div className="flex items-center gap-2">
        <Star className="w-4 h-4 text-accent fill-current" />
        <span className="font-serif text-xl text-primary">Google reviews</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Auto-shows your newest 3 Google reviews (with text) on the site. Refreshes weekly.
      </p>
      {cfg && (
        <p className="text-xs mt-2" data-testid="google-status">
          {cfg.place_id
            ? <span className="text-primary">● Connected · {cfg.count} review(s){cfg.last_refresh ? ` · last refresh ${new Date(cfg.last_refresh).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}` : ""}</span>
            : <span className="text-muted-foreground">○ Not connected</span>}
          {!cfg.configured && <span className="block text-destructive mt-1">Server API key missing.</span>}
        </p>
      )}
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input data-testid="google-input" className={`${field} flex-1`} placeholder="Paste Google Maps link or Place ID (ChIJ…)"
          value={input} onChange={(e) => setInput(e.target.value)} />
        <button data-testid="google-save" onClick={save} disabled={busy}
          className="h-11 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">{busy ? "…" : "Connect"}</button>
        {cfg?.place_id && (
          <button data-testid="google-refresh" onClick={refresh} disabled={busy}
            className="h-11 px-4 rounded-lg border border-border text-sm font-medium disabled:opacity-60">Refresh now</button>
        )}
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
const emptyReview = { name: "", rating: 5, text: "", event: "", order: 0 };

function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = { name: form.name, rating: Number(form.rating), text: form.text, event: form.event, order: Number(form.order) };
    try {
      if (form.id) await api.put(`/admin/reviews/${form.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <GoogleReviewsCard />
      <p className="text-sm text-muted-foreground mb-3">Manual testimonials (used only when Google reviews aren't connected).</p>
      <button data-testid="add-review-btn" onClick={() => setEditing({ ...emptyReview, order: reviews.length })}
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 text-sm font-medium">
        <Plus className="w-4 h-4" /> Add review
      </button>
      <div className="mt-5 space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-primary">{r.name}</span>
                <span className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-3 h-3 ${i < r.rating ? "text-accent fill-current" : "text-border"}`} />
                  ))}
                </span>
              </div>
              {r.event && <div className="text-xs text-muted-foreground">{r.event}</div>}
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.text}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing({ ...r })} className="grid place-items-center w-9 h-9 rounded-full border border-border"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(r.id)} className="grid place-items-center w-9 h-9 rounded-full border border-border text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit" : "New"} review</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input className={field} placeholder="Customer name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input className={field} placeholder="Event (e.g. Wedding, Swaroop Nagar)" value={editing.event} onChange={(e) => setEditing({ ...editing, event: e.target.value })} />
              <div>
                <label className="text-xs text-muted-foreground">Rating</label>
                <div className="mt-1 flex gap-1" data-testid="review-rating-picker">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setEditing({ ...editing, rating: n })}
                      className="p-1"><Star className={`w-6 h-6 ${n <= editing.rating ? "text-accent fill-current" : "text-border"}`} /></button>
                  ))}
                </div>
              </div>
              <textarea className={`${field} h-28 py-2`} placeholder="Review text" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
              <input className={field} type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-review-btn" onClick={() => save(editing)} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Packages ---------------- */
const emptyPkg = { name: "", price: 400, per_person: true, tagline: "", includes: "", suitable: "", featured: false, image_path: "", save_amount: 0, order: 0 };

function PackagesManager() {
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const load = () => api.get("/packages").then((r) => setPackages(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = { ...form, price: Number(form.price), order: Number(form.order), save_amount: Number(form.save_amount) || 0,
      includes: form.includes.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form.id) await api.put(`/admin/packages/${form.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, image_path: data.path }));
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); } finally { setUploadingImg(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <button data-testid="add-package-btn" onClick={() => setEditing({ ...emptyPkg })}
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 text-sm font-medium">
        <Plus className="w-4 h-4" /> Add package
      </button>
      <div className="mt-5 space-y-3">
        {packages.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3 min-w-0">
              {p.image_path && <img src={imgUrl(p.image_path)} alt={p.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />}
              <div className="min-w-0">
                <div className="font-serif text-xl text-primary truncate">{p.name} · {inr(p.price)}{p.per_person ? "/plate" : ""} {p.featured && <span className="text-accent text-xs">★</span>}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.includes.length} items{p.save_amount > 0 ? ` · Save ${inr(p.save_amount)}` : ""}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing({ ...p, includes: p.includes.join("\n") })}
                className="grid place-items-center w-9 h-9 rounded-full border border-border"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)}
                className="grid place-items-center w-9 h-9 rounded-full border border-border text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit" : "New"} package</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-xl border border-border bg-secondary overflow-hidden shrink-0 grid place-items-center">
                  {editing.image_path ? <img src={imgUrl(editing.image_path)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
                </div>
                <label data-testid="package-image-btn" className="inline-flex items-center gap-2 rounded-full border border-border px-4 h-10 cursor-pointer text-sm">
                  {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {editing.image_path ? "Change photo" : "Add photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploadingImg} />
                </label>
              </div>
              <input className={field} placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="flex gap-3">
                <input className={field} type="number" placeholder="Price / plate" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
                <input className={field} type="number" placeholder="Save ₹ (badge)" value={editing.save_amount} onChange={(e) => setEditing({ ...editing, save_amount: e.target.value })} />
              </div>
              <input className={field} placeholder="Tagline" value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} />
              <input className={field} placeholder="Suitable for (e.g. 100–300 guests)" value={editing.suitable} onChange={(e) => setEditing({ ...editing, suitable: e.target.value })} />
              <textarea className={`${field} h-28 py-2`} placeholder="Included items (one per line)" value={editing.includes} onChange={(e) => setEditing({ ...editing, includes: e.target.value })} />
              <input className={field} type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured (most popular)</label>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-package-btn" onClick={() => save(editing)} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Menu ---------------- */
const emptyCat = { name: "", order: 0, veg: "", nonveg: "" };

function MenuManager() {
  const [menu, setMenu] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/menu").then((r) => setMenu(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const items = [
      ...form.veg.split("\n").map((s) => s.trim()).filter(Boolean).map((name) => ({ name, veg: true })),
      ...form.nonveg.split("\n").map((s) => s.trim()).filter(Boolean).map((name) => ({ name, veg: false })),
    ];
    const payload = { name: form.name, order: Number(form.order), items };
    try {
      if (form.id) await api.put(`/admin/menu/${form.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/menu/${id}`); toast.success("Deleted"); load(); };

  const startEdit = (c) => setEditing({
    id: c.id, name: c.name, order: c.order,
    veg: c.items.filter((i) => i.veg).map((i) => i.name).join("\n"),
    nonveg: c.items.filter((i) => !i.veg).map((i) => i.name).join("\n"),
  });

  return (
    <div>
      <button data-testid="add-category-btn" onClick={() => setEditing({ ...emptyCat })}
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 text-sm font-medium">
        <Plus className="w-4 h-4" /> Add category
      </button>
      <div className="mt-5 space-y-3">
        {menu.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div>
              <div className="font-serif text-xl text-primary">{c.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.items.length} dishes</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(c)} className="grid place-items-center w-9 h-9 rounded-full border border-border"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(c.id)} className="grid place-items-center w-9 h-9 rounded-full border border-border text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit" : "New"} category</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input className={field} placeholder="Category name (e.g. Starters)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input className={field} type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              <div><span className="text-xs font-medium text-green-700">Veg dishes (one per line)</span>
                <textarea className={`${field} h-28 py-2 mt-1`} value={editing.veg} onChange={(e) => setEditing({ ...editing, veg: e.target.value })} /></div>
              <div><span className="text-xs font-medium text-red-700">Non-veg dishes (one per line)</span>
                <textarea className={`${field} h-24 py-2 mt-1`} value={editing.nonveg} onChange={(e) => setEditing({ ...editing, nonveg: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-category-btn" onClick={() => save(editing)} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyBulkCat = { label: "", pick: 1, order: 0, optionsText: "" };

function BulkMenuManager() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/bulk-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (c) =>
    setEditing(c ? { id: c.id, label: c.label, pick: c.pick, order: c.order, optionsText: (c.options || []).join("\n") } : { ...emptyBulkCat });

  const save = async () => {
    const options = editing.optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!editing.label.trim()) { toast.error("Enter a category name"); return; }
    if (!options.length) { toast.error("Add at least one dish option"); return; }
    setBusy(true);
    const payload = { label: editing.label.trim(), options, pick: Number(editing.pick) || 1, order: Number(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/bulk-categories/${editing.id}`, payload);
      else await api.post("/admin/bulk-categories", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/bulk-categories/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">These categories power the Bulk Food Calculator. Set "Customer picks" to 1 for a dropdown or 2 for pick-any-two chips.</p>
      <button data-testid="add-bulkcat-btn" onClick={() => openEdit(null)}
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-11 text-sm font-medium">
        <Plus className="w-4 h-4" /> Add category
      </button>
      <div className="mt-5 space-y-3">
        {cats.map((c) => (
          <div key={c.id} data-testid={`bulkcat-${c.key}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-4 gap-3">
            <div className="min-w-0">
              <div className="font-medium">{c.label} <span className="text-xs text-muted-foreground">· pick {c.pick}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{(c.options || []).join(", ")}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`bulkcat-edit-${c.key}`} onClick={() => openEdit(c)} className="grid place-items-center w-9 h-9 rounded-full border border-border" title="Edit"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`bulkcat-delete-${c.key}`} onClick={() => remove(c.id)} className="grid place-items-center w-9 h-9 rounded-full border border-border text-destructive" title="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input data-testid="bulkcat-label" className={field} placeholder="Category name (e.g. Snacks)" value={editing.label}
                onChange={(e) => setEditing((v) => ({ ...v, label: e.target.value }))} />
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Customer picks</label>
                  <select data-testid="bulkcat-pick" className={`${field} mt-1`} value={editing.pick}
                    onChange={(e) => setEditing((v) => ({ ...v, pick: Number(e.target.value) }))}>
                    <option value={1}>1 (dropdown)</option>
                    <option value={2}>2 (pick any two)</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Sort order</label>
                  <input data-testid="bulkcat-order" type="number" className={`${field} mt-1`} value={editing.order}
                    onChange={(e) => setEditing((v) => ({ ...v, order: e.target.value }))} />
                </div>
              </div>
              <textarea data-testid="bulkcat-options" className={`${field} h-36 py-2`} placeholder="One dish per line" value={editing.optionsText}
                onChange={(e) => setEditing((v) => ({ ...v, optionsText: e.target.value }))} />
            </div>
          )}
          <DialogFooter>
            <button data-testid="bulkcat-save" onClick={save} disabled={busy}
              className="rounded-full bg-primary text-primary-foreground px-6 h-11 text-sm font-medium disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

